/**
 * VideoRenderer - Accessible video block renderer using Vidstack
 * Requirements: 1.3, 1.5, 1.6, 8.1, 8.2, 8.3, 8.4
 *
 * Handles its own interaction completion tracking when requireInteraction is set.
 * Exposes ref via forwardRef for parent components that need direct player access.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MutableRefObject,
} from 'react';
import type { JSONContent } from '@tiptap/core';
import { useTranslation } from '@course-authoring/i18n';
import {
  useTheme,
  useAccountConfig,
  useImageSourceWithFallback,
  getReadableTextColor,
  type VideoTrack,
} from '@course-authoring/shared-types';
import {
  MediaPlayer,
  MediaProvider,
  Poster,
  Track,
  MEDIA_KEY_SHORTCUTS,
  type MediaLoadingStrategy,
  type MediaPlayerInstance,
  type PlayerSrc,
} from '@vidstack/react';
import '@vidstack/react/player/styles/default/theme.css';
import '@vidstack/react/player/styles/default/layouts/video.css';
import {
  DefaultVideoLayout,
  defaultLayoutIcons,
} from '@vidstack/react/player/layouts/default';

import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import { useVideoCaptionDefault } from './hooks/useVideoCaptionDefault';
import {
  useVideoTranscript,
  type VideoTranscriptOperations,
} from './hooks/useVideoTranscript';
import { InteractionMessage } from './InteractionMessage';
import { DownloadIcon } from './CourseCompletionView';
import {
  createRichTextRendererExtensions,
  safeGenerateNonEmptyRichTextHTML,
  createRichTextContentResetStyles,
} from './richTextRenderer';

export interface VideoRendererProps {
  url?: string | null;
  posterUrl?: string;
  tracks?: VideoTrack[];
  title?: string;
  shortDescription?: string;
  longDescription?: string | JSONContent;
  className?: string;
  constrainWidth?: boolean;
  /**
   * Forces the ≤576px text treatment (title + short description below the
   * player instead of overlaid on the poster) regardless of viewport width.
   * For hosts whose player is much narrower than the viewport (e.g.
   * Comparison Slider panels, which lose width to the divider).
   */
  compactText?: boolean;
  requireInteraction?: boolean;
  interactionMessage?: string;
  onInteractionComplete?: () => void;
  onPlaybackStarted?: () => void;
  onPlaybackCompleted?: () => void;
  lessonThemeColor?: string;
  /** Content locale currently being viewed; used to pick the default caption track. */
  currentLocale?: string;
  /** Persisted Centerstage ID; external videos intentionally omit it. */
  centerstageVideoId?: string;
  /** Runtime-specific transcript transport. Omitted by editor and unauthenticated runtimes. */
  transcriptOperations?: VideoTranscriptOperations;
}

export type {
  VideoTranscriptDownload,
  VideoTranscriptOperations,
} from './hooks/useVideoTranscript';
import { RichTextContent } from './RichTextContent';

/** Fraction of a video's duration treated as "watched" for completion (NLE-323). */
const VIDEO_COMPLETION_THRESHOLD = 0.95;

const VIDEO_PLAYER_LOAD_STRATEGY: MediaLoadingStrategy = 'visible';
const MISSING_VIDEO_SOURCE_MESSAGE = 'Add a video source to preview this video.';
type MediaPlayerStyle = CSSProperties & {
  [name: `--${string}`]: string | number | null | undefined;
};

function normalizeText(value?: string): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function isHlsSource(url: string): boolean {
  return /(?:\.m3u8)(?:[?#]|$)/i.test(url) || /\/hls(?:\/|$)/i.test(url);
}

function isDashSource(url: string): boolean {
  return /(?:\.mpd)(?:[?#]|$)/i.test(url) || /\/dash(?:\/|$)/i.test(url);
}

function isYouTubeSource(url: string): boolean {
  return /(?:youtube\.com|youtu\.be)/i.test(url);
}

function isVimeoSource(url: string): boolean {
  return /vimeo\.com/i.test(url);
}

function resolvePlayerSource(url: string): PlayerSrc {
  if (isHlsSource(url)) {
    return {
      src: url,
      type: 'application/x-mpegurl',
    };
  }

  if (isDashSource(url)) {
    return {
      src: url,
      type: 'application/dash+xml',
    };
  }

  if (isYouTubeSource(url)) {
    return {
      src: url,
      type: 'video/youtube',
    };
  }

  if (isVimeoSource(url)) {
    return {
      src: url,
      type: 'video/vimeo',
    };
  }

  return url;
}

/**
 * VideoRenderer component using Vidstack for accessible video playback.
 *
 * Features:
 * - Keyboard navigation (tab order, focus rings, space to play, arrows to seek)
 * - Screen reader announcements via MediaAnnouncer (built into DefaultVideoLayout)
 * - WebVTT caption support with customizable styling
 * - Consistent controls across all browsers via DefaultVideoLayout
 */

export const VideoRenderer = forwardRef<MediaPlayerInstance, VideoRendererProps>(
  (
    {
      url,
      posterUrl,
      tracks,
      title,
      shortDescription,
      longDescription,
      className,
      constrainWidth = true,
      compactText = false,
      requireInteraction = false,
      interactionMessage,
      onInteractionComplete,
      onPlaybackStarted,
      onPlaybackCompleted,
      lessonThemeColor,
      currentLocale,
      centerstageVideoId,
      transcriptOperations,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { tokens } = useTheme();
    const { videoClosedCaption, enforceFullVideoView } = useAccountConfig();
    const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
    // NA-205: video descriptions hold 20px through the tablet mid-range, drop to 16px ≤576px.
    const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
    // NA-233: hosts can force the small-mobile text treatment early via compactText.
    const compactHeaderText = compactText || isSmallMobile;
    const [isPlayerActive, setIsPlayerActive] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playerInstanceKey, setPlayerInstanceKey] = useState(0);
    const [interactionComplete, setInteractionComplete] = useState(false);
    const [playbackError, setPlaybackError] = useState(false);
    const playerRef = useRef<MediaPlayerInstance | null>(null);
    const pendingPlayRequestRef = useRef(false);
    const hasAttemptedStartupRecoveryRef = useRef(false);

    useEffect(() => {
      setIsPlayerActive(false);
      setIsPlaying(false);
      setPlaybackError(false);
      pendingPlayRequestRef.current = false;
      hasAttemptedStartupRecoveryRef.current = false;
    }, [url]);

    // A video needs completion when the block author requires interaction, or
    // when the account enforces full-video viewing (which trumps the block).
    const completionRequired = requireInteraction || enforceFullVideoView;

    const markVideoComplete = useCallback(() => {
      if (!completionRequired) return;
      setInteractionComplete((alreadyComplete) => {
        if (!alreadyComplete) {
          onInteractionComplete?.();
        }
        return true;
      });
    }, [completionRequired, onInteractionComplete]);

    // Completion fires at 95% watched (dead-space allowance, NLE-323), unified
    // across all videos; onEnded is a safety net for unknown-duration sources.
    const handleTimeUpdate = useCallback(() => {
      if (!completionRequired) return;
      const player = playerRef.current;
      if (!player) return;
      const { currentTime, duration } = player;
      if (duration > 0 && currentTime / duration >= VIDEO_COMPLETION_THRESHOLD) {
        markVideoComplete();
      }
    }, [completionRequired, markVideoComplete]);

    // Account-driven caption defaulting (resolve/apply/persist) lives in the
    // hook; see useVideoCaptionDefault for the race conditions it handles.
    const { defaultCaptionTrackIndex, applyDefaultCaptionTrack } = useVideoCaptionDefault(
      playerRef,
      tracks,
      { videoClosedCaption, currentLocale, playerInstanceKey }
    );

    const seekKeyShortcuts = useMemo(
      () =>
        enforceFullVideoView
          ? { ...MEDIA_KEY_SHORTCUTS, seekForward: null, seekBackward: null }
          : undefined,
      [enforceFullVideoView]
    );

    // enforceFullVideoView must disable seeking ENTIRELY (NLE-323). The layout
    // knobs (disabled slider, no gestures) and nulled key shortcuts cover the
    // visible affordances, but vidstack still exposes two seek inputs that
    // bypass them: the hardcoded number-key percent seek (not part of
    // keyShortcuts) and the OS/browser MediaSession seek handlers. Every seek
    // funnels through a `media-seek-request` event, and vidstack's request
    // manager honors `defaultPrevented` — so cancel it in the CAPTURE phase on
    // the shell wrapping the player. Capture on an ancestor runs before the
    // player's own (bubble-phase) handler regardless of registration order, and
    // also intercepts the MediaSession dispatch that targets the player element
    // itself. A ref carries the current flag so the always-attached listener
    // stays correct without re-binding.
    const enforceFullVideoViewRef = useRef(enforceFullVideoView);
    enforceFullVideoViewRef.current = enforceFullVideoView;
    const seekBlockerRef = useRef<{
      node: HTMLDivElement;
      handler: (event: Event) => void;
    } | null>(null);
    const setMediaShellRef = useCallback((node: HTMLDivElement | null) => {
      const previous = seekBlockerRef.current;
      if (previous) {
        previous.node.removeEventListener('media-seek-request', previous.handler, {
          capture: true,
        });
        seekBlockerRef.current = null;
      }
      if (!node) return;
      const handler = (event: Event) => {
        if (enforceFullVideoViewRef.current) event.preventDefault();
      };
      node.addEventListener('media-seek-request', handler, { capture: true });
      seekBlockerRef.current = { node, handler };
    }, []);

    const normalizedUrl = normalizeText(url ?? undefined);
    const normalizedCenterstageVideoId = normalizeText(centerstageVideoId);
    const normalizedTranscriptLocale = normalizeText(currentLocale)?.toLowerCase();
    const normalizedTitle = normalizeText(title);
    const normalizedShortDescription = normalizeText(shortDescription);
    const rendererExtensions = useMemo(() => createRichTextRendererExtensions(), []);
    const normalizedLongDescription = useMemo(() => {
      if (!longDescription) return null;
      if (typeof longDescription === 'string') return normalizeText(longDescription);
      return safeGenerateNonEmptyRichTextHTML(longDescription, rendererExtensions);
    }, [longDescription, rendererExtensions]);
    const isLongDescRichText = typeof longDescription === 'object';
    const {
      resolvedSrc: normalizedPosterUrl,
      handleError: handlePosterError,
    } = useImageSourceWithFallback(posterUrl);
    const resolvedPlayerSource = useMemo<PlayerSrc>(
      () => normalizedUrl ? resolvePlayerSource(normalizedUrl) : '',
      [normalizedUrl]
    );
    const mediaBackgroundColor = normalizedPosterUrl ? 'transparent' : '#000000';
    const showOverlay = !isPlayerActive;
    // NA-242: when no lesson theme color is set, fall back to the fixed UI
    // accent — never the account accent.
    const playerAccentColor = lessonThemeColor ?? tokens.ui.fixedAccent;
    // NA-294: playerAccentColor is a client-configurable hex (lesson color or
    // account accent), not the fixed tokens.text.onBrand pairing, so the
    // on-color must be derived from it directly rather than read off a token.
    const playerAccentTextColor = getReadableTextColor(playerAccentColor);

    const {
      transcriptAvailable,
      isDownloadingTranscript,
      transcriptDownloadError,
      handleTranscriptDownload,
    } = useVideoTranscript({
      videoUrl: normalizedUrl,
      videoId: normalizedCenterstageVideoId,
      locale: normalizedTranscriptLocale,
      operations: transcriptOperations,
    });

    const setForwardedPlayerRef = useCallback((node: MediaPlayerInstance | null) => {
      playerRef.current = node;

      if (!ref) {
        return;
      }

      if (typeof ref === 'function') {
        ref(node);
        return;
      }

      (ref as MutableRefObject<MediaPlayerInstance | null>).current = node;
    }, [ref]);

    const attemptPlaybackStart = useCallback(() => {
      const player = playerRef.current;
      if (!player) {
        return;
      }

      const playbackAttempt = player.play();
      if (
        playbackAttempt &&
        typeof playbackAttempt === 'object' &&
        'catch' in playbackAttempt &&
        typeof playbackAttempt.catch === 'function'
      ) {
        void playbackAttempt.catch(() => {
          // If the player is not ready yet, onCanPlay will retry while the request is pending.
        });
      }
    }, []);

    const remountPlayer = useCallback((options?: {
      resetRecoveryAttempt?: boolean;
      keepPendingPlayRequest?: boolean;
    }) => {
      if (options?.resetRecoveryAttempt) {
        hasAttemptedStartupRecoveryRef.current = false;
      }

      if (!options?.keepPendingPlayRequest) {
        pendingPlayRequestRef.current = false;
      }

      setIsPlayerActive(false);
      setPlaybackError(false);
      setPlayerInstanceKey((currentKey) => currentKey + 1);
    }, []);

    const requestPlaybackStart = useCallback(() => {
      pendingPlayRequestRef.current = true;
      setPlaybackError(false);
      attemptPlaybackStart();
    }, [attemptPlaybackStart]);

    const handlePlaybackError = useCallback(() => {
      setIsPlaying(false);

      if (!isPlayerActive && !hasAttemptedStartupRecoveryRef.current) {
        hasAttemptedStartupRecoveryRef.current = true;
        remountPlayer({ keepPendingPlayRequest: true });
        return;
      }

      pendingPlayRequestRef.current = false;
      setIsPlayerActive(false);
      setPlaybackError(true);
    }, [isPlayerActive, remountPlayer]);

    const handleRetry = useCallback(() => {
      pendingPlayRequestRef.current = true;
      remountPlayer({
        resetRecoveryAttempt: true,
        keepPendingPlayRequest: true,
      });
    }, [remountPlayer]);

    const styles = useMemo<Record<string, CSSProperties>>(
      () => ({
        container: {
          width: constrainWidth ? (isMobile ? '100%' : '80%') : '100%',
          maxWidth: '100%',
          margin: constrainWidth && !isMobile ? '0 auto' : 0,
          alignSelf: constrainWidth ? 'center' : 'stretch',
        },
        mediaShell: {
          position: 'relative',
          width: '100%',
          overflow: 'hidden',
          borderRadius: tokens.radius.lg,
          border: 'none',
          backgroundColor: mediaBackgroundColor,
          boxShadow: tokens.shadow.lg,
          isolation: 'isolate',
        },
        player: {
          width: '100%',
          aspectRatio: '16 / 9',
          display: 'block',
          border: 'none',
          borderRadius: tokens.radius.lg,
        },
        overlay: {
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          pointerEvents: 'auto',
          zIndex: 1,
        },
        overlayHitTarget: {
          position: 'absolute',
          inset: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          zIndex: 2,
        },
        overlayBackdrop: {
          position: 'absolute',
          inset: 0,
          background: isMobile
            ? 'linear-gradient(180deg, rgba(0, 0, 0, 0) 34%, rgba(0, 0, 0, 0.18) 50%, rgba(0, 0, 0, 0.7) 76%, rgba(0, 0, 0, 0.92) 100%)'
            : 'linear-gradient(180deg, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.14) 56%, rgba(0, 0, 0, 0.68) 78%, rgba(0, 0, 0, 0.92) 100%)',
        },
        overlayContent: {
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? tokens.spacing.xs : tokens.spacing.sm,
          width: '100%',
          maxWidth: '100%',
          padding: `${isMobile ? tokens.spacing.lg : tokens.spacing.xl} ${isMobile ? tokens.spacing.lg : tokens.spacing.xl} ${isMobile ? tokens.spacing.xl : tokens.spacing["2xl"]}`,
          boxSizing: 'border-box',
          zIndex: 1,
          pointerEvents: 'none',
        },
        overlayPlayBadgeContainer: {
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          zIndex: 1,
        },
        // Figma 5272:103 — solid white disc, no border/backdrop; triangle carries the accent.
        // Scales with the poster (design ratio: 111px on a 1108px video ≈ 10%), floored at 64px.
        overlayPlayBadge: {
          width: 'max(64px, 10%)',
          height: 'auto',
          aspectRatio: '1 / 1',
          display: 'block',
          borderRadius: '999px',
          backgroundColor: '#FFFFFF',
        },
        title: {
          margin: 0,
          color: tokens.text.onInverse,
          fontFamily: tokens.typography.fontFamily,
          fontSize: isMobile ? tokens.typography.mobile.h3.size : tokens.typography.h3.size,
          lineHeight: isMobile ? tokens.typography.mobile.h3.lineHeight : tokens.typography.h3.lineHeight,
          fontWeight: isMobile ? tokens.typography.mobile.h3.weight : tokens.typography.h3.weight,
          maxWidth: isMobile ? '100%' : 'min(76%, 44rem)',
          textWrap: 'balance',
          textShadow: '0 2px 12px rgba(0, 0, 0, 0.55)',
        },
        shortDescription: {
          margin: 0,
          width: '100%',
          color: tokens.text.onInverse,
          fontFamily: tokens.typography.fontFamily,
          fontSize: compactHeaderText ? tokens.typography.mobile.body.size : tokens.typography.body.size,
          lineHeight: compactHeaderText ? tokens.typography.mobile.body.lineHeight : tokens.typography.body.lineHeight,
          fontWeight: compactHeaderText ? tokens.typography.mobile.body.weight : tokens.typography.body.weight,
          maxWidth: '100%',
          textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)',
        },
        longDescription: {
          // NA-205: at ≤576px the long description follows the moved title/short-desc, so
          // use a tight paragraph gap (16px) instead of the 30px it needs under the video on
          // larger screens; keep 20px when it sits directly under the video with no header.
          margin: compactHeaderText
            ? `${normalizedTitle || normalizedShortDescription ? '16px' : '20px'} 0 0`
            : '30px 0 0',
          color: tokens.text.primary,
          fontFamily: tokens.typography.fontFamily,
          fontSize: compactHeaderText ? tokens.typography.mobile.body.size : tokens.typography.body.size,
          lineHeight: compactHeaderText ? tokens.typography.mobile.body.lineHeight : tokens.typography.body.lineHeight,
          fontWeight: compactHeaderText ? tokens.typography.mobile.body.weight : tokens.typography.body.weight,
          whiteSpace: 'pre-wrap',
        },
        transcriptAction: {
          display: 'inline-flex',
          alignItems: 'center',
          alignSelf: 'flex-start',
          margin: `${tokens.spacing.md} 0 0`,
          padding: 0,
          border: 0,
          background: 'transparent',
          color: tokens.text.link,
          fontFamily: tokens.typography.fontFamily,
          fontSize: tokens.typography.sizes.sm,
          fontWeight: tokens.typography.weights.regular,
          lineHeight: tokens.typography.lineHeights.normal,
          textAlign: 'left',
          textDecoration: 'underline',
          cursor: isDownloadingTranscript ? 'wait' : 'pointer',
          opacity: isDownloadingTranscript ? 0.65 : 1,
        },
        transcriptError: {
          alignSelf: 'flex-start',
          margin: `${tokens.spacing.sm} 0 0`,
          color: tokens.ui.errorText,
          fontFamily: tokens.typography.fontFamily,
          fontSize: tokens.typography.sizes.sm,
          lineHeight: tokens.typography.lineHeights.normal,
        },
        // NA-205: on narrow phones (≤576px) the title + short description move OUT of the
        // on-video overlay to sit below the player, so they need on-page (dark, no-shadow)
        // styling instead of the light-on-video overlay treatment.
        belowVideoHeader: {
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          margin: '20px 0 0',
        },
        belowVideoTitle: {
          margin: 0,
          color: tokens.text.primary,
          fontFamily: tokens.typography.fontFamily,
          fontSize: tokens.typography.mobile.h3.size,
          lineHeight: tokens.typography.mobile.h3.lineHeight,
          fontWeight: tokens.typography.mobile.h3.weight,
        },
        belowVideoDescription: {
          margin: 0,
          color: tokens.text.primary,
          fontFamily: tokens.typography.fontFamily,
          fontSize: tokens.typography.mobile.body.size,
          lineHeight: tokens.typography.mobile.body.lineHeight,
          fontWeight: tokens.typography.mobile.body.weight,
        },
        recoveryNotice: {
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: tokens.spacing.md,
          marginTop: tokens.spacing.lg,
          padding: `${tokens.spacing.md} ${tokens.spacing.lg}`,
          borderRadius: tokens.radius.md,
          border: `1px solid ${tokens.borders.default}`,
          backgroundColor: tokens.backgrounds.surfaceAlt,
        },
        recoveryText: {
          margin: 0,
          color: tokens.text.primary,
          fontFamily: tokens.typography.fontFamily,
          fontSize: isMobile ? tokens.typography.mobile.body.size : tokens.typography.body.size,
          lineHeight: isMobile ? tokens.typography.mobile.body.lineHeight : tokens.typography.body.lineHeight,
          fontWeight: isMobile ? tokens.typography.mobile.body.weight : tokens.typography.body.weight,
        },
        retryButton: {
          alignSelf: isMobile ? 'stretch' : 'center',
          padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
          borderRadius: tokens.radius.md,
          border: 'none',
          backgroundColor: playerAccentColor,
          color: playerAccentTextColor,
          fontFamily: tokens.typography.fontFamily,
          fontSize: isMobile ? tokens.typography.mobile.body.size : tokens.typography.body.size,
          lineHeight: isMobile ? tokens.typography.mobile.body.lineHeight : tokens.typography.body.lineHeight,
          fontWeight: isMobile ? tokens.typography.mobile.body.weight : tokens.typography.body.weight,
          cursor: 'pointer',
        },
        missingSourceShell: {
          width: '100%',
          aspectRatio: '16 / 9',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: tokens.spacing.sm,
          padding: tokens.spacing["2xl"],
          boxSizing: 'border-box',
          borderRadius: tokens.radius.lg,
          backgroundColor: tokens.backgrounds.inverse,
          color: tokens.text.onInverse,
          textAlign: 'center',
          fontFamily: tokens.typography.fontFamily,
        },
        missingSourceTitle: {
          margin: 0,
          fontSize: isMobile ? tokens.typography.mobile.h3.size : tokens.typography.h3.size,
          lineHeight: isMobile ? tokens.typography.mobile.h3.lineHeight : tokens.typography.h3.lineHeight,
          fontWeight: isMobile ? tokens.typography.mobile.h3.weight : tokens.typography.h3.weight,
        },
        missingSourceText: {
          margin: 0,
          fontSize: isMobile ? tokens.typography.mobile.body.size : tokens.typography.body.size,
          lineHeight: isMobile ? tokens.typography.mobile.body.lineHeight : tokens.typography.body.lineHeight,
          fontWeight: isMobile ? tokens.typography.mobile.body.weight : tokens.typography.body.weight,
          color: 'rgba(255, 255, 255, 0.82)',
        },
      }),
      [constrainWidth, isMobile, compactHeaderText, isDownloadingTranscript, mediaBackgroundColor, normalizedTitle, normalizedShortDescription, playerAccentColor, playerAccentTextColor, tokens]
    );

    const playerStyle = {
      ...styles.player,
      // In-player controls intentionally keep vidstack's neutral defaults (white); the lesson
      // theme color appears only on the poster-overlay play badge.
      '--media-poster-bg': mediaBackgroundColor,
      '--video-border': 'none',
      '--video-bg': mediaBackgroundColor,
      '--video-border-radius': tokens.radius.lg,
    } as MediaPlayerStyle;

    if (!normalizedUrl) {
      return (
        <div
          className={className}
          style={styles.container}
          data-testid="video-renderer"
        >
          <div style={styles.mediaShell} data-testid="video-media-shell">
            <div style={styles.missingSourceShell} data-testid="video-missing-source">
              {normalizedTitle ? <h3 style={styles.missingSourceTitle}>{normalizedTitle}</h3> : null}
              <p style={styles.missingSourceText}>{MISSING_VIDEO_SOURCE_MESSAGE}</p>
            </div>
          </div>

          {normalizedLongDescription && (
            isLongDescRichText ? (
              <>
                <style>{createRichTextContentResetStyles('video-long-desc')}</style>
                <RichTextContent
                  className="video-long-desc"
                  style={styles.longDescription}
                  html={normalizedLongDescription}
                />
              </>
            ) : (
              <p style={styles.longDescription}>{normalizedLongDescription}</p>
            )
          )}

          <InteractionMessage
            variant="boxed"
            requireInteraction={completionRequired}
            isComplete={interactionComplete}
            message={interactionMessage}
            fallbackMessage={t('video.watch_to_continue')}
          />
        </div>
      );
    }

    return (
      <div
        className={className}
        style={styles.container}
        data-testid="video-renderer"
      >
        <div style={styles.mediaShell} data-testid="video-media-shell" ref={setMediaShellRef}>
          <MediaPlayer
            key={playerInstanceKey}
            ref={setForwardedPlayerRef}
            src={resolvedPlayerSource}
            crossOrigin="anonymous"
            load={VIDEO_PLAYER_LOAD_STRATEGY}
            playsInline
            keyShortcuts={seekKeyShortcuts}
            style={playerStyle}
            onPlay={() => {
              setPlaybackError(false);
            }}
            onTimeUpdate={handleTimeUpdate}
            onCanPlay={() => {
              applyDefaultCaptionTrack();
              if (!pendingPlayRequestRef.current) {
                return;
              }

              attemptPlaybackStart();
            }}
            onPlaying={() => {
              pendingPlayRequestRef.current = false;
              setIsPlayerActive(true);
              setIsPlaying(true);
              setPlaybackError(false);
              hasAttemptedStartupRecoveryRef.current = false;
              onPlaybackStarted?.();
            }}
            onPause={() => {
              setIsPlaying(false);
            }}
            onEnded={() => {
              setIsPlaying(false);
              onPlaybackCompleted?.();
              markVideoComplete();
              remountPlayer({ resetRecoveryAttempt: true });
            }}
            onError={handlePlaybackError}
          >
            <MediaProvider>
              {normalizedPosterUrl && (
                <Poster
                  className="vds-poster"
                  src={normalizedPosterUrl}
                  alt={t('video.thumbnail_alt')}
                  onError={handlePosterError}
                />
              )}
            </MediaProvider>
            {isPlayerActive && (
              <DefaultVideoLayout
                icons={defaultLayoutIcons}
                disableTimeSlider={enforceFullVideoView}
                noScrubGesture={enforceFullVideoView}
                // noGestures also kills the layout's double-tap seek:±10
                // gestures, which would otherwise bypass seek prevention on
                // touch devices; play/pause stays available via the controls.
                noGestures={enforceFullVideoView}
              />
            )}
            {tracks?.map((track, index) => (
              <Track
                key={`${track.kind}-${track.language}-${index}`}
                kind={track.kind}
                src={track.src}
                label={track.label}
                lang={track.language}
                default={defaultCaptionTrackIndex >= 0 ? index === defaultCaptionTrackIndex : track.default}
              />
            ))}
          </MediaPlayer>

          {showOverlay && (
            <div style={styles.overlay} data-testid="video-overlay">
              <button
                type="button"
                onClick={requestPlaybackStart}
                style={styles.overlayHitTarget}
                aria-label={t('video.play')}
                data-testid="video-overlay-button"
              />
              {/* NA-205: no scrim on narrow phones — the overlaid text it existed for now sits below the video. */}
              {!compactHeaderText && <div style={styles.overlayBackdrop} aria-hidden="true" />}
              <div style={styles.overlayPlayBadgeContainer} aria-hidden="true">
                {/* Triangle geometry from Figma 5272:103 — equilateral, optically right of center. */}
                <svg style={styles.overlayPlayBadge} viewBox="0 0 111 111">
                  <path d="M83 56L39.5 81.1147L39.5 30.8853L83 56Z" fill={playerAccentColor} />
                </svg>
              </div>
              {!compactHeaderText && (
                <div style={styles.overlayContent} data-testid="video-overlay-content">
                  {normalizedTitle && <h3 style={styles.title}>{normalizedTitle}</h3>}
                  {normalizedShortDescription && (
                    <p style={styles.shortDescription}>{normalizedShortDescription}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {transcriptAvailable && transcriptOperations ? (
          <>
            <style>{`.video-transcript-download:focus-visible { outline: 2px solid ${tokens.ui.focusRing}; outline-offset: 2px; border-radius: 2px; }`}</style>
            <button
              type="button"
              className="video-transcript-download"
              style={styles.transcriptAction}
              onClick={handleTranscriptDownload}
              disabled={isDownloadingTranscript}
              aria-busy={isDownloadingTranscript}
              data-testid="video-transcript-download"
            >
              <span>{t('video.transcript_download')}</span>
              <DownloadIcon />
            </button>
            {transcriptDownloadError ? (
              <span
                role="alert"
                style={styles.transcriptError}
                data-testid="video-transcript-error"
              >
                {transcriptDownloadError}
              </span>
            ) : null}
          </>
        ) : null}

        {/* NA-205: on ≤576px (or compactText hosts) the title + short description render below the player instead of over it. */}
        {compactHeaderText && (normalizedTitle || normalizedShortDescription) && (
          <div style={styles.belowVideoHeader} data-testid="video-below-header">
            {normalizedTitle && <h3 style={styles.belowVideoTitle}>{normalizedTitle}</h3>}
            {normalizedShortDescription && (
              <p style={styles.belowVideoDescription}>{normalizedShortDescription}</p>
            )}
          </div>
        )}

        {normalizedLongDescription && (
          isLongDescRichText ? (
            <>
              <style>{createRichTextContentResetStyles('video-long-desc')}</style>
              <RichTextContent
                className="video-long-desc"
                style={styles.longDescription}
                html={normalizedLongDescription}
              />
            </>
          ) : (
            <p style={styles.longDescription}>{normalizedLongDescription}</p>
          )
        )}

        {playbackError && (
          <div style={styles.recoveryNotice} data-testid="video-recovery-notice">
            <p style={styles.recoveryText}>{t('video.playback_error')}</p>
            <button
              type="button"
              onClick={handleRetry}
              style={styles.retryButton}
              data-testid="video-retry-button"
            >
              {t('video.retry')}
            </button>
          </div>
        )}

        <InteractionMessage
          variant="boxed"
          requireInteraction={completionRequired}
          isComplete={interactionComplete}
          message={interactionMessage}
          fallbackMessage={t('video.watch_to_continue')}
          onClick={isPlaying ? undefined : requestPlaybackStart}
          ariaLabel={isPlaying ? undefined : t('video.play')}
        />
      </div>
    );
  }
);

VideoRenderer.displayName = 'VideoRenderer';

export type { MediaPlayerInstance };
export type { VideoTrack } from '@course-authoring/shared-types';
